/* Created by Interactive 63 - https://interactive63.com */
/**
 * The inspector's keyframe section: time, value and easing for the selected keyframe, or just
 * the easing when several keyframes are selected.
 */
import { isMorphValue, type Keyframe, type Track } from '../../../core/model/types';
import { findKeyframe } from '../../../core/timeline/keyframes';
import { PROPERTIES } from '../../../core/properties/registry';
import { setKeyframeEasing, setKeyframeValue } from '../../store/actions';
import { setKeyframeTime } from '../../store/property-actions';
import { pathModeOf, setPathMode, type PathMode } from '../../store/motion-actions';
import { formatTime, parseTime } from '../../time-format';
import { h } from '../../ui/dom';
import { openPlaybackPopover } from '../timeline/playback-popover';
import { EasingControl } from './easing-editor';
import { openMorphEditor } from '../../morph/morph-editor';
import type { InspectorContext } from './inspector-context';
import { applyPatch, createValueEditor, type ValueEditor } from './value-editor';

export class KeyframeSection {
  readonly el: HTMLElement;
  private readonly timeInput: HTMLInputElement | null = null;
  private readonly valueEditor: ValueEditor | null = null;
  private readonly easing: EasingControl | null = null;
  private readonly notice: HTMLElement | null = null;
  private startPoint: HTMLElement | null = null;
  private reverseToggle: HTMLButtonElement | null = null;
  private pathMode: HTMLSelectElement | null = null;
  private morphTools: HTMLElement | null = null;
  private placement: HTMLSelectElement | null = null;

  /** Builds the section for the given keyframe IDs. */
  constructor(
    private readonly ctx: InspectorContext,
    private readonly ids: readonly string[],
  ) {
    const heading = h('h3', { class: 'section-title' });
    this.el = h(
      'section',
      { class: 'inspector-section keyframe-section', 'aria-label': 'Keyframe' },
      heading,
    );

    const first = ids[0] ? findKeyframe(ctx.store.getState().project, ids[0]) : undefined;
    if (ids.length === 1 && first) {
      const definition = PROPERTIES[first.track.property];
      heading.textContent = `Keyframe: ${definition.label}`;
      this.timeInput = h('input', {
        class: 'keyframe-time',
        'aria-label': 'Keyframe time',
        autocomplete: 'off',
      });
      this.timeInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') this.timeInput?.blur();
        else if (event.key === 'Escape') {
          this.refresh();
          this.timeInput?.blur();
        }
      });
      this.timeInput.addEventListener('blur', () => {
        const ms = parseTime(this.timeInput?.value ?? '');
        if (ms !== null) setKeyframeTime(ctx.store, ids[0] ?? '', ms);
        this.refresh();
      });
      this.valueEditor = createValueEditor(definition, {
        scrubKey: `scrub:keyframe:${ids[0]}`,
        documentColours: ctx.documentColours,
        onChange: (patch, key) => {
          const current = findKeyframe(ctx.store.getState().project, ids[0] ?? '');
          if (current)
            setKeyframeValue(
              ctx.store,
              current.keyframe.id,
              applyPatch(current.keyframe.value, patch),
              key,
            );
        },
      });
      this.easing = new EasingControl((easing, key) => {
        setKeyframeEasing(ctx.store, ids, easing, key);
      });
      this.notice = h(
        'p',
        { class: 'keyframe-notice', hidden: true },
        'Last keyframe: easing applies to the segment after a keyframe, so this one has none.',
      );
      const trackId = first.track.id;
      const playback = h('button', { type: 'button', class: 'playback-button' }, 'Track playback…');
      playback.addEventListener('click', () => {
        const box = playback.getBoundingClientRect();
        openPlaybackPopover(ctx.store, [trackId], { x: box.left, y: box.bottom + 4 });
      });
      if (first.track.property === 'morph' && first.index > 0) {
        this.startPoint = this.buildStartPoint(ids[0] ?? '');
        const edit = h(
          'button',
          { type: 'button', title: 'Choose which points of the previous shape become which' },
          'Edit morph…',
        );
        edit.addEventListener('click', () => {
          void openMorphEditor(ctx.store, ids[0] ?? '');
        });
        this.morphTools = h('div', { class: 'keyframe-field' }, edit);
      }
      if (first.track.property === 'morph' && isMorphValue(first.keyframe.value)) {
        if (first.keyframe.value.sourceId !== undefined) {
          this.placement = this.buildPlacement(ids[0] ?? '');
        }
      }
      if (first.track.property === 'translate') {
        this.pathMode = this.buildPathMode(ids[0] ?? '');
      }
      this.el.append(
        h('label', { class: 'keyframe-field' }, 'Time', this.timeInput),
        h('div', { class: 'keyframe-field' }, 'Value', this.valueEditor.el),
        h('div', { class: 'keyframe-field' }, 'Easing to next keyframe', this.easing.el),
        ...(this.startPoint ? [this.startPoint] : []),
        ...(this.morphTools ? [this.morphTools] : []),
        ...(this.placement
          ? [h('label', { class: 'keyframe-field' }, 'Placement', this.placement)]
          : []),
        ...(this.pathMode ? [h('label', { class: 'keyframe-field' }, 'Path', this.pathMode)] : []),
        this.notice,
        h('div', { class: 'keyframe-field' }, playback),
      );
    } else {
      heading.textContent = `${ids.length} keyframes selected`;
      this.easing = new EasingControl((easing, key) => {
        setKeyframeEasing(ctx.store, ids, easing, key);
      });
      this.el.append(
        h('div', { class: 'keyframe-field' }, 'Easing to next keyframe', this.easing.el),
      );
    }
    this.refresh();
  }

  /** The start point controls for a morph keyframe: shift by a segment, reverse, or go automatic. */
  private buildStartPoint(keyframeId: string): HTMLElement {
    const { ctx } = this;
    /** The keyframe's current manual alignment, if any. */
    const current = (): { shift: number; reverse: boolean } | undefined => {
      const value = findKeyframe(ctx.store.getState().project, keyframeId)?.keyframe.value;
      return isMorphValue(value) ? value.align : undefined;
    };
    const step = (by: number): void => {
      const align = current() ?? { shift: 0, reverse: false };
      ctx.shapes.setAlignment(keyframeId, { ...align, shift: align.shift + by });
    };
    const back = h('button', { type: 'button', 'aria-label': 'Start point back one segment' }, '◀');
    const forward = h(
      'button',
      { type: 'button', 'aria-label': 'Start point forward one segment' },
      '▶',
    );
    const reverse = h('button', { type: 'button', 'aria-pressed': 'false' }, 'Reverse direction');
    const auto = h('button', { type: 'button' }, 'Auto');
    back.addEventListener('click', () => {
      step(-1);
    });
    forward.addEventListener('click', () => {
      step(1);
    });
    reverse.addEventListener('click', () => {
      const align = current() ?? { shift: 0, reverse: false };
      ctx.shapes.setAlignment(keyframeId, { ...align, reverse: !align.reverse });
    });
    auto.addEventListener('click', () => {
      ctx.shapes.setAlignment(keyframeId, undefined);
    });
    this.reverseToggle = reverse;
    return h(
      'div',
      { class: 'keyframe-field start-point' },
      h('span', null, 'Start point'),
      h('span', { class: 'start-point-controls' }, back, forward, reverse, auto),
    );
  }

  /** Where a shape taken from another layer sits: at that layer, or fitted to this one. */
  private buildPlacement(keyframeId: string): HTMLSelectElement {
    const select = h(
      'select',
      { 'aria-label': 'Placement of the picked shape' },
      h('option', { value: 'in-place' }, 'Morph in place'),
      h('option', { value: 'world' }, 'Move to where it is'),
    );
    select.addEventListener('change', () => {
      this.ctx.shapes.setPlacement(keyframeId, select.value === 'world' ? 'world' : 'in-place');
    });
    return select;
  }

  /** A choice of how the motion path passes through a position keyframe. */
  private buildPathMode(keyframeId: string): HTMLSelectElement {
    const select = h(
      'select',
      {
        class: 'keyframe-path-mode',
        'aria-label': 'Motion path through this keyframe',
        title: 'Straight lines, a smooth curve, or a corner with independent handles',
      },
      h('option', { value: 'straight' }, 'Straight'),
      h('option', { value: 'smooth' }, 'Smooth curve'),
      h('option', { value: 'corner' }, 'Corner'),
    );
    select.addEventListener('change', () => {
      setPathMode(this.ctx.store, keyframeId, select.value as PathMode);
    });
    return select;
  }

  /** Updates time, value and easing from the project. */
  refresh(): void {
    const { project } = this.ctx.store.getState();
    const found = this.ids
      .map((id) => findKeyframe(project, id))
      .filter((f): f is { track: Track; keyframe: Keyframe; index: number } => f !== undefined);
    if (found.length === 0) return;
    const playing = this.ctx.isPlaying();

    const single = found.length === 1 ? found[0] : undefined;
    if (single && this.timeInput && this.valueEditor) {
      if (document.activeElement !== this.timeInput) {
        this.timeInput.value = formatTime(single.keyframe.time);
      }
      this.valueEditor.show([single.keyframe.value]);
      this.valueEditor.setReadOnly(playing);
      this.timeInput.readOnly = playing;
    }

    if (single && this.placement && isMorphValue(single.keyframe.value)) {
      this.placement.value = single.keyframe.value.placement ?? 'in-place';
    }

    if (single && this.pathMode && document.activeElement !== this.pathMode) {
      this.pathMode.value = pathModeOf(single.keyframe);
    }

    if (single && this.reverseToggle) {
      const value = single.keyframe.value;
      this.reverseToggle.setAttribute(
        'aria-pressed',
        String(isMorphValue(value) && value.align?.reverse === true),
      );
    }

    const isLast = single !== undefined && single.index === single.track.keyframes.length - 1;
    if (this.notice) this.notice.hidden = !isLast;
    if (this.easing) {
      this.easing.el.hidden = isLast;
      if (!isLast) this.easing.setEasings(found.map((f) => f.keyframe.easing));
    }
  }
}
